import { useState } from 'react';
import { X, Plus, Trash2, ChevronUp, ChevronDown, Columns2 as Columns } from 'lucide-react';
import type { Board, BoardColumn, TaskStatus } from '../../types';
import { useApp } from '../../context/AppContext';
import { generateId } from '../../utils/helpers';

interface ColumnManagerProps {
  board: Board;
  onClose: () => void;
}

const statusOptions: { value: TaskStatus; label: string }[] = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'todo', label: 'A Fazer' },
  { value: 'in_progress', label: 'Em Progresso' },
  { value: 'in_review', label: 'Em Revisão' },
  { value: 'done', label: 'Concluído' },
];

export default function ColumnManager({ board, onClose }: ColumnManagerProps) {
  const { apiUpdateBoardColumns } = useApp();
  const [columns, setColumns] = useState<BoardColumn[]>(() =>
    board.columns.map((col) => ({ ...col }))
  );

  const handleAddColumn = () => {
    setColumns([
      ...columns,
      {
        id: 'col-' + generateId(),
        title: 'Nova Coluna',
        status: 'backlog' as TaskStatus,
        color: '#94a3b8',
      },
    ]);
  };

  const handleRemoveColumn = (index: number) => {
    if (columns.length <= 1) return;
    setColumns(columns.filter((_, i) => i !== index));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...columns];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setColumns(updated);
  };

  const handleMoveDown = (index: number) => {
    if (index === columns.length - 1) return;
    const updated = [...columns];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setColumns(updated);
  };

  const handleUpdateColumn = (index: number, field: keyof BoardColumn, value: string | number | undefined) => {
    setColumns(
      columns.map((col, i) =>
        i === index ? { ...col, [field]: value } : col
      )
    );
  };

  const handleSave = async () => {
    await apiUpdateBoardColumns(board.id, columns);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 animate-fade-in">
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl animate-scale-in flex flex-col" style={{ maxHeight: '80vh' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Columns size={20} className="text-brand-500" />
            <h3 className="text-lg font-semibold text-slate-800">Gerenciar Colunas</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4">
          <div className="space-y-3">
            {columns.map((col, index) => (
              <div
                key={col.id}
                className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 bg-slate-50/50"
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: col.color }}
                />

                <input
                  type="text"
                  value={col.title}
                  onChange={(e) => handleUpdateColumn(index, 'title', e.target.value)}
                  className="flex-1 min-w-0 px-2 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-all bg-white"
                />

                <select
                  value={col.status}
                  onChange={(e) => handleUpdateColumn(index, 'status', e.target.value)}
                  className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 bg-white"
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  min={0}
                  value={col.limit ?? ''}
                  onChange={(e) =>
                    handleUpdateColumn(
                      index,
                      'limit',
                      e.target.value ? parseInt(e.target.value, 10) : undefined
                    )
                  }
                  placeholder="Sem limite"
                  className="w-24 px-2 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 bg-white text-center"
                />

                <input
                  type="color"
                  value={col.color}
                  onChange={(e) => handleUpdateColumn(index, 'color', e.target.value)}
                  className="w-8 h-8 rounded-lg border border-slate-200 cursor-pointer flex-shrink-0 p-0.5"
                />

                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className="p-0.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Mover para cima"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === columns.length - 1}
                    className="p-0.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Mover para baixo"
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => handleRemoveColumn(index)}
                  disabled={columns.length <= 1}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                  aria-label="Remover coluna"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={handleAddColumn}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-slate-300 text-sm font-medium text-slate-500 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50/50 transition-all"
          >
            <Plus size={16} />
            Adicionar Coluna
          </button>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex-1 px-4 py-2.5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 transition-colors"
          >
            Salvar Alterações
          </button>
        </div>
      </div>
    </div>
  );
}
